import {Inject, Injectable} from '@angular/core';
import {ComponentStore} from '@ngrx/component-store';
import {Observable} from 'rxjs';
import {map, switchMap, tap, withLatestFrom} from 'rxjs/operators';
import {HttpErrorResponse} from "@angular/common/http";
import {RouteModel} from "../../../routes/models/route.model";
import {AddToRouteHttpService, AddToRouteResponse} from "../services/add-to-route-http.service";

export type AddToRouteAggregate = Omit<AddToRouteResponse, 'list'>;

export interface AddToRouteComponentState {
  routes: RouteModel[];
  cursor?: string;
  aggregate?: AddToRouteAggregate;
  loading: boolean;
  initialized: boolean;
  error: string | null;
}

@Injectable()
export class AddToRouteComponentStore extends ComponentStore<AddToRouteComponentState> {

  readonly routes$ = this.select((state) => state.routes);
  readonly aggregate$ = this.select((state) => state.aggregate);
  readonly loading$ = this.select((state) => state.loading);
  readonly initialized$ = this.select((state) => state.initialized);
  readonly error$ = this.select((state) => state.error);
  readonly cursor$ = this.select((state) => state.cursor);

  readonly vm$ = this.select({
    routes: this.routes$,
    loading: this.loading$,
    initialized: this.initialized$,
    error: this.error$,
    cursor: this.cursor$
  });

  constructor(@Inject(AddToRouteHttpService) private addToRouteHttpService: AddToRouteHttpService) {
    super({
      routes: [],
      cursor: undefined,
      aggregate: undefined,
      loading: false,
      initialized: false,
      error: null
    });
  }

  readonly setRoutes = this.updater((state: AddToRouteComponentState, value: { routes: RouteModel[], cursor?: string }) => ({
    ...state,
    routes: value.routes,
    cursor: value.cursor,
    loading: false,
    initialized: true,
    error: null,
  }));

  readonly setAggregate = this.updater((state: AddToRouteComponentState, aggregate: AddToRouteAggregate) => ({
    ...state,
    aggregate,
  }));

  readonly onAppendRoutes = this.updater((state: AddToRouteComponentState, value: { routes: RouteModel[], cursor?: string }) => ({
    ...state,
    routes: [...state.routes, ...value.routes],
    cursor: value.cursor,
    loading: false,
    initialized: true,
    error: null,
  }));

  readonly setLoading = this.updater((state: AddToRouteComponentState, loading: boolean) => ({
    ...state,
    loading,
  }));

  readonly setError = this.updater((state: AddToRouteComponentState, error: HttpErrorResponse) => ({
    ...state,
    error: error.message,
    loading: false,
  }));

  readonly resetRoutes = this.updater(() => ({
    routes: [],
    cursor: undefined,
    aggregate: undefined,
    loading: false,
    initialized: false,
    error: null,
  }));

  readonly getRoutes = this.effect((trigger$: Observable<any>) =>
    trigger$.pipe(
      tap(() => this.setLoading(true)),
      withLatestFrom(this.cursor$),
      switchMap(([params, cursor]) =>
        this.addToRouteHttpService.getAssignRouteData({...params, cursor: cursor || ''}).pipe(
          tap({
            next: (response) => this.onAppendRoutes({routes: response.list.list, cursor: response.list.cursor}),
            error: (err) => this.setError(err),
          })
        )
      )
    )
  );

  fetchAssignRouteData(params: any): Observable<AddToRouteAggregate> {
    this.setLoading(true);
    return this.addToRouteHttpService.getAssignRouteData(params).pipe(
      tap({error: (err) => this.setError(err)}),
      map((response) => {
        this.setRoutes({routes: response.list.list, cursor: response.list.cursor});
        const {list, ...aggregate} = response;
        this.setAggregate(aggregate);
        return aggregate;
      })
    );
  }

}
